import {
  AdminHeaderActionButton,
  AdminPageHeader,
  AdminPageShell,
} from '@/components/patterns/admin-page'
import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import { EmptyState } from '@/components/patterns/empty-state'
import { FsnImportProgress } from '@/components/imports/fsn/FsnImportProgress'
import { FsnImportClientStep } from '@/components/imports/fsn/FsnImportClientStep'
import { FsnImportLogoPreview } from '@/components/imports/fsn/FsnImportLogoPreview'
import {
  AddOnPreviewList,
  CustomerPreviewList,
  GenericPreviewList,
  ImportSummaryGrid,
  NotificationContactPreviewList,
  PreviewPanel,
  ReviewPreviewList,
  SocialLinkPreviewList,
} from '@/components/imports/fsn/FsnImportPreview'
import {
  BreakdownMetric,
  BreakdownRow,
  PreviewMetric,
} from '@/components/imports/fsn/FsnImportMetrics'
import { OrganizationContextNotice } from '@/components/patterns/info-notice'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { useOrganizationContext } from '@/hooks/useOrganizationContext'
import { usePathHelper } from '@/hooks/usePathHelper'
import { OrganizationType } from '@/lib/enums/OrganizationType'
import {
  invalidateFsnImportTouchedQueries,
  isFsnClientSearchReady,
  useActiveFsnImportAttemptQuery,
  useFsnClientSearchQuery,
  useFsnImportEstimateQuery,
  useFsnImportAttemptQuery,
  usePreviewFsnImportPlanMutation,
  useStartFsnImportAttemptMutation,
} from '@/lib/services/fsn-imports/fsn-imports.query'
import {
  getFloristNameComparison,
  getFsnImportErrorDescription,
} from '@/lib/services/fsn-imports/fsn-imports.presentation'
import {
  createFsnImportAttemptId as createImportAttemptId,
  customProductScopeOptions,
  defaultFsnImportSections as defaultSections,
  defaultFsnProductOptions as defaultProductOptions,
  fsnImportFlowSteps as flowSteps,
  fsnProductScopeOptions,
  getEstimatedFsnProductImpact as getEstimatedProductImpact,
  getFsnDeliveryAreaPreviewTotal as deliveryAreaPreviewTotal,
  getFsnImportFlowStep as getFlowStep,
  getFsnImportOptionDescription as getOptionDescription,
  getFsnImportSectionEstimate as getSectionEstimate,
  getNextFsnImportStepKey as getNextStepKey,
  getPreviousFsnImportStepKey as getPreviousStepKey,
  isTerminalFsnImportAttempt as isTerminalImportAttempt,
} from '@/lib/services/fsn-imports/fsn-imports.flow'
import { cn, getErrorMessage } from '@/lib/utils'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useDebounce } from '@uidotdev/usehooks'
import {
  AlertCircle,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Network,
  Play,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

export const Route = createFileRoute('/_authenticated/$orgId/imports/fsn-import')({
  component: FsnImportPage,
})

function showStartAttemptToast(attempt) {
  if (attempt?.status === 'completed') {
    toast.success('Flower Shop Network import is already complete.')

    return
  }

  if (attempt?.status === 'failed') {
    const description =
      attempt.error?.message ?? attempt.message ?? 'Review the status below before starting again.'

    toast.error('Flower Shop Network import already failed.', { description })

    return
  }

  toast.success('Flower Shop Network import started.')
}

function CheckboxRow({ id, checked, onCheckedChange, label, description, disabled = false }) {
  return (
    <label
      htmlFor={id}
      className={cn(
        'grid cursor-pointer grid-cols-[1rem_minmax(0,1fr)] gap-3 rounded-md border border-border/70 bg-background px-3 py-3 text-sm',
        disabled && 'cursor-not-allowed opacity-60'
      )}
    >
      <Checkbox
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(value) => onCheckedChange(Boolean(value))}
        className="mt-0.5"
      />
      <span className="min-w-0">
        <span className="block font-medium leading-5">{label}</span>
        {description ? (
          <span className="mt-1 block text-sm leading-5 text-muted-foreground">{description}</span>
        ) : null}
      </span>
    </label>
  )
}

function FlowStepper({ currentStep, onStepChange, canVisitStep }) {
  return (
    <nav className="border-b border-border/70" aria-label="FSN import steps">
      <ol className="-mb-px flex min-w-0 gap-1 overflow-x-auto pretty-scrollbar">
        {flowSteps.map((step) => {
          const active = step.key === currentStep
          const disabled = !canVisitStep(step.key)

          return (
            <li key={step.key} className="flex-none">
              <button
                type="button"
                data-raw-button-owner="import-wizard-step"
                className={cn(
                  'rounded-b-none rounded-t-xl border border-transparent px-3 py-2 text-sm font-medium leading-tight whitespace-nowrap transition-colors disabled:cursor-not-allowed disabled:opacity-45 sm:px-4',
                  active
                    ? 'border-border/70 border-b-background bg-background text-foreground shadow-xs'
                    : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                )}
                aria-label={`${step.label} step`}
                aria-current={active ? 'step' : undefined}
                disabled={disabled}
                onClick={() => onStepChange(step.key)}
              >
                {step.label}
              </button>
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

function SelectedFloristPill({ client }) {
  if (!client) {
    return null
  }

  return (
    <div className="flex min-w-0 px-1">
      <Badge
        variant="secondary"
        size="md"
        className="max-w-full justify-start px-3 py-1.5"
        aria-label="Selected FSN florist"
        title={client.name}
      >
        <Network className="size-4 shrink-0" aria-hidden="true" />
        <span className="shrink-0 text-muted-foreground">FSN florist</span>
        <span className="min-w-0 truncate font-semibold">{client.name}</span>
      </Badge>
    </div>
  )
}

function StepShell({ currentStep, children, aside = null }) {
  const step = getFlowStep(currentStep)

  return (
    <Card className="@container/fsn-step" size="sm">
      <CardHeader size="sm">
        <CardTitle size="sm">{step.title}</CardTitle>
        <CardDescription className="max-w-[74ch] text-sm">{step.description}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-6" size="sm">
        <div className="min-w-0">{children}</div>
        {aside ? <aside className="min-w-0 border-t border-border/70 pt-5">{aside}</aside> : null}
      </CardContent>
    </Card>
  )
}

function FlowFooter({
  currentStep,
  onBack,
  onNext,
  onPreview,
  onStart,
  previewPending,
  startPending,
  canNext,
  canPreview,
  canStart,
  isImportRunning,
  planReady,
  canBack = true,
}) {
  const firstStep = currentStep === 'client'
  const previewStep = currentStep === 'preview'
  const statusStep = currentStep === 'status'
  const backDisabled = firstStep || !canBack

  return (
    <div className="sticky bottom-0 z-30 mt-auto rounded-t-xl border border-b-0 border-border/70 bg-background/95 px-4 py-3 shadow-[0_-12px_30px_-26px_var(--foreground)] backdrop-blur supports-[backdrop-filter]:bg-background/85">
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
        <Button
          type="button"
          variant="outline"
          className="min-w-28 justify-center"
          disabled={backDisabled}
          onClick={onBack}
        >
          <ChevronLeft className="size-4" />
          Back
        </Button>

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          {previewStep && planReady ? (
            <Button
              type="button"
              variant="outline"
              className="min-w-32"
              disabled={!canPreview || previewPending || isImportRunning}
              onClick={onPreview}
            >
              {previewPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Network className="size-4" />
              )}
              Preview again
            </Button>
          ) : null}

          {previewStep ? (
            planReady ? (
              <Button type="button" className="min-w-36" disabled={!canStart} onClick={onStart}>
                {startPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Play className="size-4" />
                )}
                Start import
              </Button>
            ) : (
              <Button
                type="button"
                className="min-w-36"
                disabled={!canPreview || previewPending || isImportRunning}
                onClick={onPreview}
              >
                {previewPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Network className="size-4" />
                )}
                Preview
              </Button>
            )
          ) : statusStep && canBack ? (
            <Button type="button" variant="outline" className="min-w-36" onClick={onBack}>
              <ChevronLeft className="size-4" />
              View preview
            </Button>
          ) : (
            <Button type="button" className="min-w-28" disabled={!canNext} onClick={onNext}>
              Next
              <ChevronRight className="size-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

function NameMismatchConfirmationDialog({ comparison, open, onOpenChange, onConfirm }) {
  return (
    <ConfirmationDialog
      title="Confirm FSN client match"
      description="These names do not look like the same florist. Confirm before continuing so FSN data is not imported into the wrong organization."
      icon={<AlertCircle className="size-4" />}
      trigger={null}
      open={open}
      onOpenChange={onOpenChange}
      onConfirm={onConfirm}
      confirmText="Continue anyway"
      cancelText="Go back"
    >
      <div className="grid gap-3 pt-1 text-left">
        <div className="rounded-md border border-border/70 bg-background px-3 py-3">
          <Typography variant="label" tone="muted">
            Impersonating
          </Typography>
          <div className="mt-1 break-words text-sm font-semibold text-foreground">
            {comparison.organizationLabel}
          </div>
        </div>
        <div className="rounded-md border border-border/70 bg-background px-3 py-3">
          <Typography variant="label" tone="muted">
            Selected from FSN
          </Typography>
          <div className="mt-1 break-words text-sm font-semibold text-foreground">
            {comparison.fsnClientLabel}
          </div>
        </div>
      </div>
    </ConfirmationDialog>
  )
}

function ProductImpactPanel({ estimate, productOptions, productsSelected }) {
  const productImpact = getEstimatedProductImpact({
    estimate,
    productOptions,
    productsSelected,
  })
  const fsnPageProducts =
    productsSelected && productOptions.fsn_product_scope !== 'none'
      ? (estimate?.products?.fsn?.current_pages ?? 0)
      : 0
  const customPageProducts =
    productsSelected && productOptions.custom_product_scope !== 'none'
      ? (estimate?.products?.custom?.current_pages ?? 0)
      : 0

  return (
    <div className="@container/fsn-products space-y-4" aria-label="Current product import estimate">
      <div>
        <h3 className="text-sm font-medium">Current settings estimate</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          These numbers show what will be imported with the settings above.
        </p>
      </div>

      <div className="grid gap-2 @2xl/fsn-products:grid-cols-4">
        <PreviewMetric label="Products" value={productImpact.total} />
        <PreviewMetric label="FSN products" value={productImpact.fsn} />
        <PreviewMetric label="Custom products" value={productImpact.custom} />
        <PreviewMetric
          label="Hidden products"
          value={productImpact.hiddenFsn + productImpact.hiddenCustom}
        />
      </div>

      <div className="grid gap-2 @2xl/fsn-products:grid-cols-4">
        <BreakdownMetric label="FSN page products" value={fsnPageProducts} />
        <BreakdownMetric
          label="Hidden FSN products"
          value={productImpact.hiddenFsn}
          tone={productImpact.hiddenFsn > 0 ? 'warning' : 'default'}
        />
        <BreakdownMetric label="Custom page products" value={customPageProducts} />
        <BreakdownMetric
          label="Hidden custom products"
          value={productImpact.hiddenCustom}
          tone={productImpact.hiddenCustom > 0 ? 'warning' : 'default'}
        />
      </div>
    </div>
  )
}

function AccountSelector({ source, value, onValueChange, disabled = false }) {
  const accounts = source?.accounts ?? []

  if (accounts.length <= 1) {
    return null
  }

  return (
    <div className="grid gap-2">
      <label className="text-sm font-medium" htmlFor="fsn-website-account">
        Website account
      </label>
      <Select value={value ?? ''} onValueChange={onValueChange} disabled={disabled}>
        <SelectTrigger id="fsn-website-account" className="w-full">
          <SelectValue placeholder="Choose FSN website account" />
        </SelectTrigger>
        <SelectContent>
          {accounts.map((account) => (
            <SelectItem key={account.username} value={account.username}>
              {account.username}
              {account.sitename ? ` / ${account.sitename}` : ''}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-sm text-muted-foreground">
        This FSN client has more than one website account. Choose the current storefront before
        importing.
      </p>
    </div>
  )
}

function ProductsStep({
  estimate,
  productOptions,
  productsSelected,
  selectedSections,
  fsnProductScopeDescription,
  customProductScopeDescription,
  websiteNavigation,
  onToggleSection,
  onUpdateProductOption,
  onWebsiteNavigationChange,
  available,
  disabled,
}) {
  return (
    <StepShell
      currentStep="products"
      aside={
        <ProductImpactPanel
          estimate={estimate}
          productOptions={productOptions}
          productsSelected={productsSelected}
        />
      }
    >
      <div className="space-y-5">
        {!available ? (
          <Alert variant="warning">
            <AlertCircle className="size-4" />
            <AlertTitle>Website products unavailable</AlertTitle>
            <AlertDescription>
              FSN does not have a website account for this client. Continue to import its network
              and business data.
            </AlertDescription>
          </Alert>
        ) : null}
        <CheckboxRow
          id="fsn-section-products"
          checked={productsSelected}
          disabled={disabled || !available}
          onCheckedChange={(checked) => onToggleSection('products', checked)}
          label="Import products"
          description="Products are imported before contacts and website details."
        />

        <div className="grid gap-4 @3xl/fsn-step:grid-cols-2">
          <div className="grid gap-2">
            <label className="text-sm font-medium" htmlFor="fsn-product-scope">
              FSN products
            </label>
            <Select
              value={productOptions.fsn_product_scope}
              disabled={disabled || !selectedSections.includes('products')}
              onValueChange={(value) => onUpdateProductOption('fsn_product_scope', value)}
            >
              <SelectTrigger id="fsn-product-scope" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {fsnProductScopeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fsnProductScopeDescription ? (
              <p className="text-sm text-muted-foreground">{fsnProductScopeDescription}</p>
            ) : null}
          </div>

          <div className="grid gap-2">
            <label className="text-sm font-medium" htmlFor="fsn-custom-product-scope">
              Custom products
            </label>
            <Select
              value={productOptions.custom_product_scope}
              disabled={disabled || !selectedSections.includes('products')}
              onValueChange={(value) => onUpdateProductOption('custom_product_scope', value)}
            >
              <SelectTrigger id="fsn-custom-product-scope" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {customProductScopeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {customProductScopeDescription ? (
              <p className="text-sm text-muted-foreground">{customProductScopeDescription}</p>
            ) : null}
          </div>
        </div>

        <CheckboxRow
          id="fsn-product-option-add-searchable-details"
          checked={productOptions.add_searchable_details}
          disabled={disabled || !selectedSections.includes('products')}
          onCheckedChange={(checked) => onUpdateProductOption('add_searchable_details', checked)}
          label="Add searchable details"
          description="Adds conservative product details for search and browsing. Existing details stay untouched."
        />

        <CheckboxRow
          id="fsn-product-option-create-current-page-collections"
          checked={productOptions.create_current_page_collections}
          disabled={disabled || !selectedSections.includes('products')}
          onCheckedChange={(checked) =>
            onUpdateProductOption('create_current_page_collections', checked)
          }
          label="Create active-page collections"
          description="Creates collections from current non-homepage pages."
        />

        <div className="grid gap-2">
          <label className="text-sm font-medium" htmlFor="fsn-website-navigation">
            Website menu
          </label>
          <Select
            value={websiteNavigation}
            disabled={disabled || !productsSelected}
            onValueChange={onWebsiteNavigationChange}
          >
            <SelectTrigger id="fsn-website-navigation" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Set up menu automatically</SelectItem>
              <SelectItem value="skip">Leave menu unchanged</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground">
            Automatic setup uses the collections created from active FSN pages.
          </p>
        </div>
      </div>
    </StepShell>
  )
}

function formatFulfillmentSignal(value) {
  if (value == null) {
    return 'Not returned'
  }

  return value ? 'Enabled' : 'Disabled'
}

function deliveryFeeTotal(summary = {}) {
  return (
    Number(summary?.cities ?? 0) +
    Number(summary?.postal_codes ?? 0) +
    Number(summary?.distance_ranges ?? 0)
  )
}

function FulfillmentStep({
  estimate,
  locationSelected,
  deliveryAreasSelected,
  onToggleSection,
  disabled,
}) {
  const deliveryPreviewRows = estimate?.delivery_areas?.preview_rows ?? []
  const distancePreviewRows = estimate?.delivery_areas?.distance_preview_rows ?? []
  const distancePreviewTotal = Math.max(
    Number(estimate?.delivery_areas?.distance_preview_total ?? 0),
    distancePreviewRows.length
  )
  const locationRows = estimate?.location?.preview_rows ?? []
  const deliveryCount = deliveryFeeTotal(estimate?.delivery_areas)
  const deliveryAreaTotal = deliveryAreaPreviewTotal(estimate?.delivery_areas)

  return (
    <StepShell
      currentStep="fulfillment"
      aside={
        <div className="space-y-4">
          <div className="grid gap-2 @2xl/fsn-step:grid-cols-3">
            <BreakdownMetric
              label="Pickup"
              value={formatFulfillmentSignal(estimate?.location?.pickup_enabled)}
            />
            <BreakdownMetric
              label="Delivery"
              value={formatFulfillmentSignal(estimate?.location?.delivery_enabled)}
            />
            <BreakdownMetric label="Delivery fees" value={deliveryCount} />
          </div>
          <GenericPreviewList
            title="Location preview"
            rows={locationRows}
            emptyLabel="No location details found for this FSN client."
          />
          <GenericPreviewList
            title="Delivery city and ZIP fee preview"
            rows={deliveryPreviewRows}
            emptyLabel="No delivery cities or ZIP codes found."
            total={deliveryAreaTotal}
          />
          <GenericPreviewList
            title="Driving-distance fee preview"
            rows={distancePreviewRows}
            emptyLabel="No driving-distance fees found."
            total={distancePreviewTotal}
          />
        </div>
      }
    >
      <div className="space-y-3">
        <CheckboxRow
          id="fsn-section-location"
          checked={locationSelected}
          disabled={disabled}
          onCheckedChange={(checked) => onToggleSection('location', checked)}
          label="Import location and fulfillment"
          description="Updates the main address, phone, email, Google profile, pickup/delivery flags, hours, and cutoffs."
        />
        <CheckboxRow
          id="fsn-section-delivery-areas"
          checked={deliveryAreasSelected}
          disabled={disabled}
          onCheckedChange={(checked) => onToggleSection('delivery_areas', checked)}
          label="Import delivery fees"
          description="Imports FSN delivery cities, ZIP codes, and driving-distance settings when available."
        />
      </div>
    </StepShell>
  )
}

function RelationshipsStep({
  estimate,
  customersSelected,
  reviewsSelected,
  customersAvailable,
  onToggleSection,
  disabled,
}) {
  return (
    <StepShell
      currentStep="relationships"
      aside={
        <div className="grid gap-5 @5xl/fsn-step:grid-cols-2">
          <CustomerPreviewList
            customers={estimate?.customers?.preview_rows ?? []}
            selected={customersSelected}
            total={estimate?.customers?.total}
          />
          <ReviewPreviewList
            reviews={estimate?.reviews?.preview_rows ?? []}
            selected={reviewsSelected}
            summary={estimate?.reviews}
          />
        </div>
      }
    >
      <div className="space-y-3">
        {!customersAvailable ? (
          <Alert variant="warning">
            <AlertCircle className="size-4" />
            <AlertTitle>Website customer history unavailable</AlertTitle>
            <AlertDescription>
              Customer history currently comes from orders associated with an FSN website account.
              Reviews and other network data can still be imported.
            </AlertDescription>
          </Alert>
        ) : null}
        <CheckboxRow
          id="fsn-section-customers"
          checked={customersSelected}
          disabled={disabled || !customersAvailable}
          onCheckedChange={(checked) => onToggleSection('customers', checked)}
          label="Import customers"
          description="Deduplicates by FSN source key, email, then phone before creating customer records."
        />
        <CheckboxRow
          id="fsn-section-reviews"
          checked={reviewsSelected}
          disabled={disabled}
          onCheckedChange={(checked) => onToggleSection('reviews', checked)}
          label="Import reviews"
          description="Imports active FSN reviews as published and keeps non-active reviews hidden or pending."
        />
      </div>
    </StepShell>
  )
}

function AddOnsStep({ estimate, addOnsSelected, onToggleSection, disabled }) {
  return (
    <StepShell
      currentStep="addons"
      aside={
        <AddOnPreviewList
          addOns={estimate?.add_ons?.preview_rows ?? []}
          selected={addOnsSelected}
          total={estimate?.add_ons?.total}
        />
      }
    >
      <div className="space-y-3">
        <CheckboxRow
          id="fsn-section-add-ons"
          checked={addOnsSelected}
          disabled={disabled}
          onCheckedChange={(checked) => onToggleSection('add_ons', checked)}
          label="Import add-ons"
          description="Creates hidden standalone add-on products and sorts them into Sympathy and Non-sympathy add-on collections."
        />
      </div>
    </StepShell>
  )
}

function NotificationsStep({ estimate, selected, onToggleSection, disabled }) {
  const contacts = estimate?.notification_contacts?.preview_rows ?? []

  return (
    <StepShell currentStep="notifications">
      <div className="space-y-4">
        <CheckboxRow
          id="fsn-section-notification-contacts"
          checked={selected}
          disabled={disabled}
          onCheckedChange={(checked) => onToggleSection('notification_contacts', checked)}
          label="Import notification contacts"
          description="FSN receiving contacts are imported enabled; other contacts are imported disabled. Event mappings are not created."
        />

        <NotificationContactPreviewList
          contacts={contacts}
          selected={selected}
          total={estimate?.notification_contacts?.total}
          enabledTotal={estimate?.notification_contacts?.enabled}
          disabledTotal={estimate?.notification_contacts?.disabled}
        />
      </div>
    </StepShell>
  )
}

function WebsiteStep({
  estimate,
  socialSelected,
  logoSelected,
  logoAvailable,
  onToggleSection,
  disabled,
}) {
  const socialLinks = estimate?.social_links?.preview_rows ?? []

  return (
    <StepShell
      currentStep="website"
      aside={
        <div className="space-y-4">
          <FsnImportLogoPreview
            key={estimate?.logo?.url ?? 'empty-logo'}
            logoUrl={estimate?.logo?.url}
          />
          <div className="grid gap-2">
            <BreakdownRow label="Logo found" value={estimate?.logo?.available ? 'Yes' : 'No'} />
          </div>
          <SocialLinkPreviewList
            links={socialLinks}
            selected={socialSelected}
            total={estimate?.social_links?.total}
          />
        </div>
      }
    >
      <div className="space-y-3">
        {!logoAvailable ? (
          <Alert variant="warning">
            <AlertCircle className="size-4" />
            <AlertTitle>Website logo unavailable</AlertTitle>
            <AlertDescription>
              FSN does not have a website account for this client. Client-level social links remain
              available.
            </AlertDescription>
          </Alert>
        ) : null}
        <CheckboxRow
          id="fsn-section-social-links"
          checked={socialSelected}
          disabled={disabled}
          onCheckedChange={(checked) => onToggleSection('social_links', checked)}
          label="Import social links"
          description="Fills blank footer links for Facebook, Instagram, YouTube, Pinterest, and TikTok."
        />
        <CheckboxRow
          id="fsn-section-logo"
          checked={logoSelected}
          disabled={disabled || !logoAvailable}
          onCheckedChange={(checked) => onToggleSection('logo', checked)}
          label="Import logo"
          description="Adds the FSN logo only when the default design logo slots are blank."
        />
      </div>
    </StepShell>
  )
}

function PreviewStep({ plan, estimate, selectedSections, productOptions, productsSelected }) {
  const productImpact = getEstimatedProductImpact({
    estimate,
    productOptions,
    productsSelected,
  })
  const draftSummary = {
    products_ready: productImpact.total,
    location_ready: selectedSections.includes('location') && Boolean(estimate?.location?.available),
    delivery_areas_ready: selectedSections.includes('delivery_areas')
      ? getSectionEstimate(estimate, 'delivery_areas')
      : 0,
    customers_ready: selectedSections.includes('customers')
      ? getSectionEstimate(estimate, 'customers')
      : 0,
    reviews_ready: selectedSections.includes('reviews')
      ? getSectionEstimate(estimate, 'reviews')
      : 0,
    add_ons_ready: selectedSections.includes('add_ons')
      ? getSectionEstimate(estimate, 'add_ons')
      : 0,
    contacts_ready: selectedSections.includes('notification_contacts')
      ? getSectionEstimate(estimate, 'notification_contacts')
      : 0,
    social_links_ready: selectedSections.includes('social_links')
      ? getSectionEstimate(estimate, 'social_links')
      : 0,
    logo_available: selectedSections.includes('logo') && Boolean(estimate?.logo?.available),
  }

  return (
    <StepShell
      currentStep="preview"
      aside={
        <div className="space-y-4">
          <Typography variant="label" tone="muted">
            Current settings estimate
          </Typography>
          <ImportSummaryGrid summary={plan?.summary ?? draftSummary} />
        </div>
      }
    >
      <PreviewPanel plan={plan} />
    </StepShell>
  )
}

function StatusStep({ attempt, plan }) {
  const summary = attempt?.plan_summary ??
    plan?.summary ?? {
      products_ready: 0,
      location_ready: false,
      delivery_areas_ready: 0,
      customers_ready: 0,
      reviews_ready: 0,
      add_ons_ready: 0,
      contacts_ready: 0,
      social_links_ready: 0,
      logo_available: false,
    }

  return (
    <StepShell currentStep="status" aside={<ImportSummaryGrid summary={summary} />}>
      {attempt ? (
        <FsnImportProgress attempt={attempt} />
      ) : (
        <EmptyState
          size="compact"
          title="No import running"
          description="Start the previewed import to see live status here."
        />
      )}
    </StepShell>
  )
}

export function FsnImportPage() {
  const { organization } = useOrganizationContext()
  const path = usePathHelper()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedClient, setSelectedClient] = useState(null)
  const [clientSearchOpen, setClientSearchOpen] = useState(true)
  const [selectedUsername, setSelectedUsername] = useState(null)
  const [selectedSections, setSelectedSections] = useState(defaultSections)
  const [productOptions, setProductOptions] = useState(defaultProductOptions)
  const [websiteNavigation, setWebsiteNavigation] = useState('auto')
  const [plan, setPlan] = useState(null)
  const [errorState, setErrorState] = useState(null)
  const [activeImportAttemptId, setActiveImportAttemptId] = useState(null)
  const [recoveredImportAttemptId, setRecoveredImportAttemptId] = useState(null)
  const [startedImportAttempt, setStartedImportAttempt] = useState(null)
  const [importAttemptId, setImportAttemptId] = useState(createImportAttemptId)
  const [currentStep, setCurrentStep] = useState('client')
  const [nameMismatchDialogOpen, setNameMismatchDialogOpen] = useState(false)
  const [pendingStepAfterNameCheck, setPendingStepAfterNameCheck] = useState(null)
  const [confirmedNameMismatchKey, setConfirmedNameMismatchKey] = useState(null)
  const handledImportAttemptIdsRef = useRef(new Set())
  const previewRequestIdRef = useRef(0)
  const isGlobalOrg = organization?.type === OrganizationType.Global.Value
  const debouncedSearchTerm = useDebounce(searchTerm, 300)
  const visibleQuery = searchTerm.trim()
  const debouncedQuery = String(debouncedSearchTerm ?? '').trim()
  const canSearch = isFsnClientSearchReady(visibleQuery)
  const clientSearchQuery = useFsnClientSearchQuery({
    query: debouncedQuery,
    enabled: !isGlobalOrg,
  })
  const previewMutation = usePreviewFsnImportPlanMutation()
  const startMutation = useStartFsnImportAttemptMutation()
  const estimateQuery = useFsnImportEstimateQuery({
    fsnClientId: selectedClient?.id,
    fsnUsername: selectedUsername,
    enabled: Boolean(selectedClient && !isGlobalOrg),
  })
  const activeFsnImportAttemptQuery = useActiveFsnImportAttemptQuery({
    enabled: Boolean(organization?.id && !isGlobalOrg),
  })
  const refetchActiveFsnImportAttempt = activeFsnImportAttemptQuery.refetch
  const rawRecoveredImportAttempt = activeFsnImportAttemptQuery.data ?? null
  const trackedImportAttemptId =
    activeImportAttemptId ?? rawRecoveredImportAttempt?.id ?? recoveredImportAttemptId ?? null
  const importAttemptQuery = useFsnImportAttemptQuery(trackedImportAttemptId, {
    enabled: Boolean(trackedImportAttemptId),
  })
  const waitingOnDebounce = canSearch && visibleQuery !== debouncedQuery
  const searchMatchesCurrentInput = canSearch && !waitingOnDebounce
  const clients = searchMatchesCurrentInput ? (clientSearchQuery.data?.clients ?? []) : []
  const isSearching = clientSearchQuery.isFetching || waitingOnDebounce
  const showEmptyState =
    searchMatchesCurrentInput && clientSearchQuery.isSuccess && !isSearching && clients.length === 0
  const unavailableMessage =
    searchMatchesCurrentInput && clientSearchQuery.isError
      ? getErrorMessage(clientSearchQuery.error) ||
        'Flower Shop Network database is unavailable. Try again later.'
      : null
  const activeImportAttempt = trackedImportAttemptId ? (importAttemptQuery.data ?? null) : null
  const pendingStartedImportAttempt =
    startedImportAttempt?.id === trackedImportAttemptId ? startedImportAttempt : null
  const displayedImportAttempt =
    activeImportAttempt ?? pendingStartedImportAttempt ?? rawRecoveredImportAttempt ?? null
  const activeStep = displayedImportAttempt && !selectedClient && !plan ? 'status' : currentStep
  const isImportRunning =
    startMutation.isPending ||
    displayedImportAttempt?.status === 'queued' ||
    displayedImportAttempt?.status === 'running'
  const resultCountLabel = clients.length === 1 ? '1 match' : `${clients.length} matches`
  const showClientSearch = clientSearchOpen || !selectedClient
  const fsnProductScopeDescription = getOptionDescription(
    fsnProductScopeOptions,
    productOptions.fsn_product_scope
  )
  const customProductScopeDescription = getOptionDescription(
    customProductScopeOptions,
    productOptions.custom_product_scope
  )
  const estimate = estimateQuery.data ?? null
  const nameComparison = getFloristNameComparison(organization?.name, selectedClient?.name)
  const accountChoiceSource = estimate ?? plan
  const availableSections = accountChoiceSource?.available_sections ?? defaultSections
  const effectiveSelectedSections = selectedSections.filter((section) =>
    availableSections.includes(section)
  )
  const productsAvailable = availableSections.includes('products')
  const customersAvailable = availableSections.includes('customers')
  const logoAvailable = availableSections.includes('logo')
  const productsSelected = effectiveSelectedSections.includes('products')
  const effectiveWebsiteNavigation = productsSelected ? websiteNavigation : 'skip'
  const requiresAccountChoice = accountChoiceSource?.status === 'account_required'
  const needsAccountChoice =
    requiresAccountChoice || (accountChoiceSource?.accounts?.length ?? 0) > 1
  const hasRequiredAccountChoice = !needsAccountChoice || Boolean(selectedUsername)
  const accountStateKnown = Boolean(
    estimate?.status === 'ready' ||
    estimate?.status === 'account_required' ||
    plan?.status === 'ready' ||
    plan?.status === 'account_required' ||
    estimateQuery.isError
  )
  const selectedAccountEstimateReady = Boolean(
    !selectedUsername ||
    estimate?.status === 'ready' ||
    plan?.status === 'ready' ||
    estimateQuery.isError
  )
  const canLeaveClientStep = Boolean(
    selectedClient && accountStateKnown && hasRequiredAccountChoice && selectedAccountEstimateReady
  )
  const planReady = Boolean(plan?.fingerprint && plan.status === 'ready')
  const canStart =
    Boolean(planReady && selectedClient && hasRequiredAccountChoice) && !isImportRunning
  const canPreview =
    Boolean(selectedClient) && hasRequiredAccountChoice && !previewMutation.isPending
  const locationSelected = effectiveSelectedSections.includes('location')
  const deliveryAreasSelected = effectiveSelectedSections.includes('delivery_areas')
  const customersSelected = effectiveSelectedSections.includes('customers')
  const reviewsSelected = effectiveSelectedSections.includes('reviews')
  const addOnsSelected = effectiveSelectedSections.includes('add_ons')
  const contactsSelected = effectiveSelectedSections.includes('notification_contacts')
  const socialSelected = effectiveSelectedSections.includes('social_links')
  const logoSelected = effectiveSelectedSections.includes('logo')
  const canNext =
    activeStep === 'client'
      ? canLeaveClientStep
      : activeStep === 'status'
        ? Boolean(displayedImportAttempt)
        : true

  useEffect(() => {
    if (!rawRecoveredImportAttempt?.id) {
      return undefined
    }

    let shouldStoreRecoveredAttempt = true

    queueMicrotask(() => {
      if (!shouldStoreRecoveredAttempt) {
        return
      }

      setRecoveredImportAttemptId((currentId) =>
        currentId === rawRecoveredImportAttempt.id ? currentId : rawRecoveredImportAttempt.id
      )
    })

    return () => {
      shouldStoreRecoveredAttempt = false
    }
  }, [rawRecoveredImportAttempt?.id])

  useEffect(() => {
    if (!activeImportAttempt || handledImportAttemptIdsRef.current.has(activeImportAttempt.id)) {
      return
    }

    if (activeImportAttempt.status === 'completed' || activeImportAttempt.status === 'failed') {
      handledImportAttemptIdsRef.current.add(activeImportAttempt.id)

      if (organization?.id) {
        void invalidateFsnImportTouchedQueries({ organizationId: organization.id })
      }

      void refetchActiveFsnImportAttempt()
    }

    if (activeImportAttempt.status === 'completed') {
      toast.success('Flower Shop Network import finished.')

      return
    }

    if (activeImportAttempt.status === 'failed') {
      const description =
        activeImportAttempt.error?.message ??
        activeImportAttempt.message ??
        'Try previewing the FSN data again.'

      toast.error('Unable to import FSN data.', { description })
    }
  }, [activeImportAttempt, organization?.id, refetchActiveFsnImportAttempt])

  const resetPlan = () => {
    previewRequestIdRef.current += 1
    setPlan(null)
    setErrorState(null)
  }

  const handleSelectClient = (client) => {
    if (isImportRunning) {
      return
    }

    setCurrentStep('client')
    setSelectedClient(client)
    setSelectedUsername(null)
    setSearchTerm('')
    setClientSearchOpen(false)
    setNameMismatchDialogOpen(false)
    setPendingStepAfterNameCheck(null)
    setConfirmedNameMismatchKey(null)
    resetPlan()

    if (isTerminalImportAttempt(displayedImportAttempt)) {
      setRecoveredImportAttemptId(null)
      setActiveImportAttemptId(null)
      setStartedImportAttempt(null)
    }
  }

  const toggleSection = (section, checked) => {
    setSelectedSections((current) =>
      checked
        ? Array.from(new Set([...current, section]))
        : current.filter((item) => item !== section)
    )

    if (section === 'products') {
      setWebsiteNavigation(checked ? 'auto' : 'skip')
    }

    resetPlan()
  }

  const updateProductOption = (option, value) => {
    setProductOptions((current) => ({
      ...current,
      [option]: value,
    }))
    resetPlan()
  }

  const handlePreview = async () => {
    if (!selectedClient) {
      const message = 'Choose an FSN client first.'

      setErrorState({ title: 'Client needed', message })
      toast.error(message)

      return
    }

    if (!hasRequiredAccountChoice) {
      const message = 'Choose the current FSN website account first.'

      setCurrentStep('client')
      setErrorState({ title: 'Website account needed', message })
      toast.error(message)

      return
    }

    const previewRequestId = previewRequestIdRef.current + 1
    previewRequestIdRef.current = previewRequestId

    try {
      const result = await previewMutation.mutateAsync({
        fsnClientId: selectedClient.id,
        fsnUsername: selectedUsername,
        sections: effectiveSelectedSections,
        productOptions,
        websiteNavigation: effectiveWebsiteNavigation,
      })

      if (previewRequestId !== previewRequestIdRef.current) {
        return
      }

      setPlan(result)
      setErrorState(null)
      setImportAttemptId(createImportAttemptId())

      if (!isImportRunning && isTerminalImportAttempt(displayedImportAttempt)) {
        setRecoveredImportAttemptId(null)
        setActiveImportAttemptId(null)
        setStartedImportAttempt(null)
      }

      if (result.status === 'account_required') {
        setCurrentStep('client')
        toast.message('Choose an FSN website account.')
      } else {
        setCurrentStep('preview')
      }
    } catch (error) {
      if (previewRequestId !== previewRequestIdRef.current) {
        return
      }

      const description = getFsnImportErrorDescription(
        error,
        'Flower Shop Network database is unavailable. Try again later.'
      )

      setErrorState({ title: 'Preview failed', message: description })
      toast.error('Unable to preview FSN import.', { description })
    }
  }

  const handleStart = async () => {
    if (!selectedClient || !plan?.fingerprint || plan.status !== 'ready') {
      const message = 'Preview the FSN import before starting.'

      setErrorState({ title: 'Preview needed', message })
      toast.error(message)

      return
    }

    try {
      const nextImportAttemptId = isTerminalImportAttempt(displayedImportAttempt)
        ? createImportAttemptId()
        : importAttemptId

      const attempt = await startMutation.mutateAsync({
        fsnClientId: selectedClient.id,
        fsnUsername: plan.account?.username ?? selectedUsername,
        sections: plan.selected_sections,
        productOptions: plan.product_options,
        websiteNavigation: plan.website_navigation,
        planFingerprint: plan.fingerprint,
        importAttemptId: nextImportAttemptId,
      })

      if (isTerminalImportAttempt(attempt)) {
        handledImportAttemptIdsRef.current.add(attempt.id)

        if (organization?.id) {
          void invalidateFsnImportTouchedQueries({ organizationId: organization.id })
        }

        void refetchActiveFsnImportAttempt()
      }

      setImportAttemptId(nextImportAttemptId)
      setStartedImportAttempt(attempt)
      setActiveImportAttemptId(attempt.id)
      setCurrentStep('status')
      setErrorState(null)
      showStartAttemptToast(attempt)
    } catch (error) {
      const description = getFsnImportErrorDescription(error, 'Try previewing the FSN data again.')

      setErrorState({ title: 'Import failed', message: description })
      toast.error('Unable to import FSN data.', { description })
    }
  }

  const canVisitStep = (stepKey) => {
    if (stepKey === 'client') {
      return true
    }

    if (stepKey === 'status') {
      return Boolean(displayedImportAttempt)
    }

    return canLeaveClientStep
  }

  const requestStepChange = (stepKey) => {
    if (!canVisitStep(stepKey)) {
      return
    }

    if (
      activeStep === 'client' &&
      stepKey !== 'client' &&
      nameComparison.needsConfirmation &&
      confirmedNameMismatchKey !== nameComparison.key
    ) {
      setPendingStepAfterNameCheck(stepKey)
      setNameMismatchDialogOpen(true)

      return
    }

    setCurrentStep(stepKey)
  }

  const handleStepChange = (stepKey) => {
    requestStepChange(stepKey)
  }

  const handleNextStep = () => {
    requestStepChange(getNextStepKey(activeStep))
  }

  const handleConfirmNameMismatch = () => {
    setConfirmedNameMismatchKey(nameComparison.key)
    setCurrentStep(pendingStepAfterNameCheck ?? 'products')
  }

  const handlePreviousStep = () => {
    if (activeStep === 'status') {
      if (!canVisitStep('preview')) {
        return
      }

      setCurrentStep('preview')

      return
    }

    setCurrentStep(getPreviousStepKey(activeStep))
  }

  const canGoBack = activeStep !== 'client' && (activeStep !== 'status' || canVisitStep('preview'))

  if (isGlobalOrg) {
    return (
      <AdminPageShell maxWidth="wide">
        <AdminPageHeader
          icon={Network}
          title="Flower Shop Network Import"
          description="Find a Flower Shop Network client before choosing what to import."
          actions={
            <AdminHeaderActionButton asChild variant="outline">
              <Link {...path.imports.index().link}>
                <ArrowLeft className="size-4" />
                Back to Imports
              </Link>
            </AdminHeaderActionButton>
          }
        />

        <OrganizationContextNotice title="Choose an organization first">
          Flower Shop Network imports only run against the florist account you are currently
          impersonating.
        </OrganizationContextNotice>
      </AdminPageShell>
    )
  }

  return (
    <AdminPageShell
      maxWidth="full"
      className="@container/fsn-import flex min-h-[calc(100svh-7rem)] flex-col gap-5 space-y-0 pb-6"
    >
      <AdminPageHeader
        icon={Network}
        title="Flower Shop Network Import"
        description={`Import FSN data into ${organization?.name ?? 'this shop'}.`}
        actions={
          <AdminHeaderActionButton asChild variant="outline">
            <Link {...path.imports.index().link}>
              <ArrowLeft className="size-4" />
              Back to Imports
            </Link>
          </AdminHeaderActionButton>
        }
      />

      {errorState ? (
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertTitle>{errorState.title}</AlertTitle>
          <AlertDescription>{errorState.message}</AlertDescription>
        </Alert>
      ) : null}

      <NameMismatchConfirmationDialog
        comparison={nameComparison}
        open={nameMismatchDialogOpen}
        onOpenChange={(open) => {
          setNameMismatchDialogOpen(open)

          if (!open) {
            setPendingStepAfterNameCheck(null)
          }
        }}
        onConfirm={handleConfirmNameMismatch}
      />

      <div className="grid gap-2">
        <SelectedFloristPill client={selectedClient} />
        <FlowStepper
          currentStep={activeStep}
          onStepChange={handleStepChange}
          canVisitStep={canVisitStep}
        />
      </div>

      {activeStep === 'client' ? (
        <StepShell currentStep="client">
          <FsnImportClientStep
            showClientSearch={showClientSearch}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            canSearch={canSearch}
            isSearching={isSearching}
            unavailableMessage={unavailableMessage}
            showEmptyState={showEmptyState}
            clients={clients}
            resultCountLabel={resultCountLabel}
            selectedClient={selectedClient}
            accountSelector={
              <AccountSelector
                source={accountChoiceSource}
                value={selectedUsername ?? accountChoiceSource?.account?.username ?? ''}
                onValueChange={(value) => {
                  setSelectedUsername(value)
                  resetPlan()
                }}
                disabled={isImportRunning}
              />
            }
            onSelectClient={handleSelectClient}
            onChangeClient={() => setClientSearchOpen(true)}
            estimate={estimate}
            estimateLoading={estimateQuery.isFetching}
            estimateError={estimateQuery.isError ? estimateQuery.error : null}
            isImportRunning={isImportRunning}
          />
        </StepShell>
      ) : null}

      {activeStep === 'products' ? (
        <ProductsStep
          estimate={estimate}
          productOptions={productOptions}
          productsSelected={productsSelected}
          selectedSections={effectiveSelectedSections}
          fsnProductScopeDescription={fsnProductScopeDescription}
          customProductScopeDescription={customProductScopeDescription}
          websiteNavigation={websiteNavigation}
          onToggleSection={toggleSection}
          onUpdateProductOption={updateProductOption}
          onWebsiteNavigationChange={(value) => {
            setWebsiteNavigation(value)
            resetPlan()
          }}
          available={productsAvailable}
          disabled={isImportRunning}
        />
      ) : null}

      {activeStep === 'fulfillment' ? (
        <FulfillmentStep
          estimate={estimate}
          locationSelected={locationSelected}
          deliveryAreasSelected={deliveryAreasSelected}
          onToggleSection={toggleSection}
          disabled={isImportRunning}
        />
      ) : null}

      {activeStep === 'relationships' ? (
        <RelationshipsStep
          estimate={estimate}
          customersSelected={customersSelected}
          reviewsSelected={reviewsSelected}
          customersAvailable={customersAvailable}
          onToggleSection={toggleSection}
          disabled={isImportRunning}
        />
      ) : null}

      {activeStep === 'addons' ? (
        <AddOnsStep
          estimate={estimate}
          addOnsSelected={addOnsSelected}
          onToggleSection={toggleSection}
          disabled={isImportRunning}
        />
      ) : null}

      {activeStep === 'notifications' ? (
        <NotificationsStep
          estimate={estimate}
          selected={contactsSelected}
          onToggleSection={toggleSection}
          disabled={isImportRunning}
        />
      ) : null}

      {activeStep === 'website' ? (
        <WebsiteStep
          estimate={estimate}
          socialSelected={socialSelected}
          logoSelected={logoSelected}
          logoAvailable={logoAvailable}
          onToggleSection={toggleSection}
          disabled={isImportRunning}
        />
      ) : null}

      {activeStep === 'preview' ? (
        <PreviewStep
          plan={plan}
          estimate={estimate}
          selectedSections={effectiveSelectedSections}
          productOptions={productOptions}
          productsSelected={productsSelected}
        />
      ) : null}

      {activeStep === 'status' ? <StatusStep attempt={displayedImportAttempt} plan={plan} /> : null}

      <FlowFooter
        currentStep={activeStep}
        onBack={handlePreviousStep}
        onNext={handleNextStep}
        onPreview={handlePreview}
        onStart={handleStart}
        previewPending={previewMutation.isPending}
        startPending={startMutation.isPending}
        canNext={canNext}
        canPreview={canPreview}
        canStart={canStart}
        canBack={canGoBack}
        isImportRunning={isImportRunning}
        planReady={planReady}
      />
    </AdminPageShell>
  )
}
